import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'pt', name: 'Português', flag: 'PT' },
  { code: 'pt-BR', name: 'Português BR', flag: 'BR' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'pl', name: 'Polski', flag: 'PL' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'tr', name: 'Türkçe', flag: 'TR' },
  { code: 'it', name: 'Italiano', flag: 'IT' },
];

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState('en'); // Start with fallback
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const updateLanguage = (lng) => {
      if (!mounted) return;
      setCurrentLanguage(lng);
      setIsInitialized(true);
    };

    const handleLanguageChange = (lng) => {
      updateLanguage(lng);
    };

    const handleInitialized = () => {
      if (i18n.resolvedLanguage || i18n.language) {
        updateLanguage(i18n.resolvedLanguage || i18n.language);
      }
    };

    // Immediate initialization check
    const initializeLanguage = () => {
      if (i18n.isInitialized && (i18n.resolvedLanguage || i18n.language)) {
        updateLanguage(i18n.resolvedLanguage || i18n.language);
        return true;
      }
      return false;
    };

    // Try immediate initialization
    if (!initializeLanguage()) {
      // If not ready immediately, set up event listeners and fallback
      if (!i18n.isInitialized) {
        i18n.on('initialized', handleInitialized);
      }
      
      // Single fallback timeout instead of polling interval
      timeoutId = setTimeout(() => {
        if (!mounted || isInitialized) return;
        
        // Final attempt - use whatever language is available
        const finalLanguage = i18n.resolvedLanguage || i18n.language || 'en';
        updateLanguage(finalLanguage);
      }, 500); // Reduced from 2000ms to 500ms
    }

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      i18n.off('languageChanged', handleLanguageChange);
      i18n.off('initialized', handleInitialized);
    };
  }, [i18n, isInitialized]);

  const changeLanguage = (languageCode) => {
    i18n.changeLanguage(languageCode);
  };

  const getCurrentLanguage = () => {
    // Handle language code mapping with special cases for regional variants
    const normalizeLanguageCode = (code) => {
      if (!code) return 'en';
      
      // Special case: keep pt-BR as-is for Brazilian Portuguese
      if (code === 'pt-BR') return 'pt-BR';
      
      // For other regional variants, try exact match first, then base language
      if (SUPPORTED_LANGUAGES.find(lang => lang.code === code)) {
        return code;
      }
      
      // Extract base language code (pt-PT -> pt, en-US -> en)
      const baseCode = code.split('-')[0];
      return baseCode;
    };

    const normalizedCode = normalizeLanguageCode(currentLanguage);
    return SUPPORTED_LANGUAGES.find(lang => lang.code === normalizedCode) || SUPPORTED_LANGUAGES[0];
  };

  const value = {
    currentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    changeLanguage,
    getCurrentLanguage,
    isInitialized,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
